import type { ReactNode } from 'react'
import {
  type BillingPeriod,
  concierge,
  type Plan,
  type PlanId,
  plansFor,
} from '../../checkout/catalogue'
import {
  annualSavingsCents,
  annualSavingsPercent,
  bestSavingsPercent,
  firstChargeCents,
  formatCents,
  rateCents,
} from '../../checkout/pricing'
import type { PromoCode } from '../../promo/codes'
import { Button } from '../ui/Button'
import { TextLink } from '../ui/TextLink'

/**
 * Step 1: billing period + plan choice.
 *
 * The billing segments are native `<input type="radio">` elements, visually
 * hidden with the segment as the label: the browser supplies focus, arrow-key
 * navigation and selection semantics inside the shadow root, with zero custom
 * focus code.
 *
 * The plan cards are buttons, because choosing one is an action: it settles
 * the plan and opens step 2. Radios would do that on the arrow keys too, and
 * throw a keyboard user browsing the plans into step 2 on the first press.
 * The chosen plan stays marked, so Continue and a later Edit know which it
 * is. Concierge is not sold here, so its card is not a control at all; only
 * the link in it does anything.
 */

export interface PlanPickerProps {
  billing: BillingPeriod
  planId: PlanId
  /** The visitor's active promo, which prices the plans it changes. */
  promo: PromoCode | null
  /** The contact page the Concierge card leads to. */
  contactUrl: string
  onBillingChange(billing: BillingPeriod): void
  /** The visitor chose a plan: by clicking its card, or Continue with the one already chosen. */
  onChoose(planId: PlanId): void
}

/** A plan's artwork beside its name: decorative, square, and sized before it loads. */
export function PlanThumbnail({ plan }: { plan: Plan }) {
  return (
    <img
      src={plan.imageUrl}
      alt=""
      width={72}
      height={72}
      className="size-18 shrink-0 rounded-lg object-cover"
    />
  )
}

export function PlanPicker({
  billing,
  planId,
  promo,
  contactUrl,
  onBillingChange,
  onChoose,
}: PlanPickerProps) {
  return (
    <div className="flex flex-col gap-4">
      <BillingToggle
        billing={billing}
        bestSavings={bestSavingsPercent(promo)}
        onChange={onBillingChange}
      />

      <ul className="flex flex-col gap-3 pt-2">
        {plansFor(promo).map((plan) => (
          <li key={plan.id}>
            <PlanButton
              plan={plan}
              billing={billing}
              selected={plan.id === planId}
              onClick={() => onChoose(plan.id)}
            />
          </li>
        ))}
        <li>
          <ConciergeCard billing={billing} contactUrl={contactUrl} />
        </li>
      </ul>

      <Button type="button" block onClick={() => onChoose(planId)} data-testid="plan-continue">
        Continue
      </Button>

      <TrustBadges billing={billing} />
    </div>
  )
}

function BillingToggle({
  billing,
  bestSavings,
  onChange,
}: {
  billing: BillingPeriod
  /** The largest whole percent paying annually saves on any plan. */
  bestSavings: number
  onChange: (billing: BillingPeriod) => void
}) {
  return (
    <fieldset className="grid grid-cols-2 rounded-full border border-black/5 bg-white p-1 shadow-sm">
      <legend className="sr-only">Billing period</legend>
      {(['annual', 'monthly'] as const).map((period) => (
        <label key={period} className="cursor-pointer">
          <input
            type="radio"
            name="cv-billing"
            value={period}
            checked={billing === period}
            onChange={() => onChange(period)}
            className="peer sr-only"
            data-testid={`billing-${period}`}
          />
          <span className="flex h-11 flex-col items-center justify-center rounded-full text-sm leading-tight font-medium text-ink-soft transition peer-checked:bg-brand peer-checked:font-semibold peer-checked:text-white peer-focus-visible:ring-2 peer-focus-visible:ring-brand-ring">
            {period === 'annual' ? 'Pay annually' : 'Pay monthly'}
            {period === 'annual' && (
              // Inherits the segment's colour, so it reads in both states.
              <span className="text-xs font-normal opacity-80">Save up to {bestSavings}%</span>
            )}
          </span>
        </label>
      ))}
    </fieldset>
  )
}

/**
 * A plan card: stacked on a phone, where a price column beside the name would
 * leave the name no room, and a row from `sm` up.
 */
const CARD =
  'flex w-full flex-col gap-4 rounded-2xl border bg-white p-4 text-left sm:flex-row sm:items-center'

function PlanButton({
  plan,
  billing,
  selected,
  onClick,
}: {
  plan: Plan
  billing: BillingPeriod
  selected: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={selected || undefined}
      className={`${CARD} relative transition outline-none hover:border-brand/60 focus-visible:ring-2 focus-visible:ring-brand-ring ${
        selected ? 'border-brand ring-1 ring-brand' : 'border-black/10'
      }`}
      data-testid={`plan-${plan.id}`}
    >
      {plan.mostPopular && (
        <span className="absolute -top-3 right-6 z-10 rounded-lg bg-linear-to-r from-popular-a to-popular-b px-2.5 py-1 font-mono text-xs font-semibold text-ink">
          ✳ Most Popular
        </span>
      )}
      <PlanCardBody plan={plan} billing={billing} />
    </button>
  )
}

/** Listed at its prices, but not a control: the way in is the link. */
function ConciergeCard({ billing, contactUrl }: { billing: BillingPeriod; contactUrl: string }) {
  return (
    <div className={`${CARD} border-black/10`} data-testid={`plan-${concierge.id}`}>
      <PlanCardBody
        plan={concierge}
        billing={billing}
        description={
          <>
            {concierge.description} <TextLink href={contactUrl}>Contact us</TextLink> to get
            started.
          </>
        }
      />
    </div>
  )
}

/** What every card shows: artwork, name and description, then the price beneath or beside them. */
function PlanCardBody({
  plan,
  billing,
  description = plan.description,
}: {
  plan: Plan
  billing: BillingPeriod
  description?: ReactNode
}) {
  return (
    <>
      <PlanThumbnail plan={plan} />

      <span className="min-w-0 sm:flex-1">
        <span className="block text-lg font-bold text-ink">{plan.name}</span>
        <span className="mt-1 block text-sm text-ink-soft">{description}</span>
      </span>

      {/* Beneath a hairline on a phone; from `sm` up, a column wide enough for
          the widest rate and "/month" on one line. */}
      <span className="border-t border-black/5 pt-4 sm:w-44 sm:shrink-0 sm:border-t-0 sm:border-l sm:pt-0 sm:pl-4">
        <span className="block whitespace-nowrap">
          <span className="text-2xl font-bold text-ink" data-testid={`rate-${plan.id}`}>
            {formatCents(rateCents(plan, billing))}
          </span>{' '}
          <span className="text-sm text-ink-soft">/month</span>
        </span>

        {billing === 'annual' && (
          <>
            <span className="mt-1 block text-sm text-ink-soft" data-testid={`due-today-${plan.id}`}>
              {formatCents(firstChargeCents(plan.annual))} due today
            </span>
            <span
              className="mt-1 block text-sm font-medium text-brand"
              data-testid={`annual-savings-${plan.id}`}
            >
              Save {formatCents(annualSavingsCents(plan))} ({annualSavingsPercent(plan)}%)
            </span>
          </>
        )}
      </span>
    </>
  )
}

const TRUST_BADGES: Record<BillingPeriod, readonly string[]> = {
  annual: ['Free Shipping', 'Cancel anytime', 'For kids 6–13'],
  monthly: ['Billed monthly', 'Cancel anytime', 'For kids 6–13'],
}

function TrustBadges({ billing }: { billing: BillingPeriod }) {
  return (
    <ul className="flex flex-wrap justify-center gap-2" data-testid="trust-badges">
      {TRUST_BADGES[billing].map((badge) => (
        <li key={badge} className="rounded-lg bg-field px-3 py-1.5 text-sm text-ink-soft">
          {badge}
        </li>
      ))}
    </ul>
  )
}
